import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useTranslation } from '../hooks/useTranslation';
import { useTheme } from '../hooks/useTheme';
import { Languages, Mic, Play, Download, CheckCircle, XCircle, Globe } from 'lucide-react';

export const ExplanationPage = () => {
  const { t, language, changeLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  const steps = [
    {
      icon: <Languages className="h-8 w-8" />,
      title: t('explanation_step1_title'),
      description: t('explanation_step1_desc'),
    },
    {
      icon: <Mic className="h-8 w-8" />,
      title: t('explanation_step2_title'),
      description: t('explanation_step2_desc'),
    },
    {
      icon: <Play className="h-8 w-8" />,
      title: t('explanation_step3_title'),
      description: t('explanation_step3_desc'),
    },
    {
      icon: <Download className="h-8 w-8" />,
      title: t('explanation_step4_title'),
      description: t('explanation_step4_desc'),
    },
  ];

  const supportedBrowsers = [
    { name: 'Google Chrome', supported: true },
    { name: 'Microsoft Edge', supported: true },
    { name: 'Safari', supported: true },
    { name: 'Opera', supported: true },
    { name: 'Firefox', supported: false },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="explanation-page">
      <Header
        t={t}
        theme={theme}
        toggleTheme={toggleTheme}
        language={language}
        changeLanguage={changeLanguage}
      />

      <main className="flex-1">
        {/* Header Section - No background image */}
        <div className="bg-muted/30 py-16 sm:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-3 max-w-3xl mx-auto">
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
                {t('explanation_title')}
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground">
                {t('explanation_subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="p-8 border border-border rounded-sm bg-card"
                  data-testid={`step-${index + 1}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-secondary rounded-sm">
                      {step.icon}
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Browser Compatibility Section */}
            <div className="mt-12 p-8 border border-border rounded-sm bg-card">
              <h2 className="font-heading font-bold text-2xl mb-4 flex items-center gap-2">
                <Globe className="h-6 w-6" />
                {t('browser_compatibility_title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t('browser_compatibility_desc')}
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {supportedBrowsers.map((browser) => (
                  <div 
                    key={browser.name}
                    className={`p-4 rounded-sm border text-center ${
                      browser.supported 
                        ? 'border-green-500/30 bg-green-500/5' 
                        : 'border-red-500/30 bg-red-500/5'
                    }`}
                  >
                    {browser.supported ? (
                      <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-600 dark:text-green-400" />
                    ) : (
                      <XCircle className="h-6 w-6 mx-auto mb-2 text-red-600 dark:text-red-400" />
                    )}
                    <p className="text-sm font-medium">{browser.name}</p>
                    <p className={`text-xs mt-1 ${
                      browser.supported 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {browser.supported ? t('browser_supported') : t('browser_not_supported')}
                    </p>
                  </div>
                ))}
              </div>

              <p className="text-sm text-muted-foreground mt-6">
                {t('browser_recommendation')}
              </p>
            </div>

            {/* Technology Section */}
            <div className="mt-8 p-8 border border-border rounded-sm bg-muted/30">
              <h2 className="font-heading font-bold text-2xl mb-4">
                {t('explanation_tech_title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t('explanation_tech_desc')}
              </p>
              <div className="mt-6 p-4 bg-secondary rounded-sm">
                <p className="font-mono text-sm text-muted-foreground">
                  Web Speech API - Browser Native Technology
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer t={t} />
    </div>
  );
};
